import Button from "@/components/Button";
import { arrow } from "@/designSystemIcons";
import { star } from "@/figmaElements";
import { calculatePlayerStatsBonus } from "@/functions/calculatePlayerStatsBonus";
import { capitalizeWords } from "@/functions/capitalizeFirstLetters";
import { chemStyles } from "@/functions/getChemStyles";
import { cn } from "@/lib/utils";
import {
  chemStylesToIcon,
  inGameAttributesDict,
} from "@/redux/squadBuilder/squadBuilderUtils";
import { useEffect, useState } from "react";

import { GridBox } from "../../DetailedPlayer";
import {
  DetailedPlayerAction,
  StateChemistry,
  StateChemstyle,
} from "../../reducer";
import { ChemStyles } from "./ChemStyles/ChemStyles";
import { ChemistrySection } from "./Chemistry/ChemistrySection";

type AttributeType = { label: string; value: number; bonus: number };

interface OverallAttributeProps {
  sectionLabel: string;
  bonusForSection: number;
  attributes: AttributeType[];
  overallAttribute: number;
  accelerate?: string;
}

export const getAttributeColor = (value: number) => {
  if (value < 50) {
    return `bg-primary-red`;
  } else if (value < 70) {
    return `bg-primary-yellow`;
  } else if (value < 80) {
    return `bg-ea-light-green`;
  } else {
    return `bg-ea-dark-green`;
  }
};

const getAttributeColorText = (value: number) => {
  if (value < 50) {
    return `text-primary-red`;
  } else if (value < 70) {
    return `text-primary-yellow`;
  } else if (value < 80) {
    return `text-ea-light-green`;
  } else {
    return `text-ea-dark-green`;
  }
};

const OverallAttribute = ({
  sectionLabel,
  bonusForSection,
  attributes,
  overallAttribute,
  accelerate,
}: OverallAttributeProps) => {
  return (
    <div className="flex flex-col gap-y-3 px-2 mobile:px-4 py-2 h-full">
      <div className="flex flex-row items-center justify-between">
        <p className="h-4 mobile:h-5 text-xs mobile:text-xs font-semibold text-dark-secondary">
          {sectionLabel}
        </p>
        {overallAttribute && (
          <div className="flex flex-row items-center">
            {bonusForSection > 0 && (
              <div className="flex flex-row items-center mr-2 text-xs mobile:text-xs">
                <div className="text-ea-dark-green">{bonusForSection}</div>
                <span className="w-2 h-2 text-ea-dark-green rotate-180">
                  {arrow}
                </span>
              </div>
            )}
            <div
              className={cn(
                "h-5 w-1",
                getAttributeColor(overallAttribute + bonusForSection),
              )}
            />
            <div
              className="w-9 h-5 text-xs mobile:text-xs font-semibold text-dark-secondary flex items-center justify-center bg-panel-middleground rounded-r"
              style={{
                textShadow:
                  "0px 1px 1px rgba(0, 0, 0, 0.80), 0px 1px 5.8px #000",
              }}
            >
              {overallAttribute + bonusForSection > 99
                ? 99
                : overallAttribute + bonusForSection}
            </div>
          </div>
        )}
      </div>
      {attributes.map((attribute) => (
        <div className="flex flex-col" key={attribute.label}>
          <div className="flex flex-row items-center justify-end text-xs mobile:text-xs">
            <div className="text-dark-tertiary mr-auto truncate">
              {attribute.label}
            </div>
            {attribute.bonus > 0 && (
              <>
                <div className="text-ea-dark-green">{attribute.bonus}</div>
                <span className="w-2 h-2 text-ea-dark-green rotate-180">
                  {arrow}
                </span>
              </>
            )}
            <div className="w-9 text-center text-dark-secondary">
              {attribute.value + attribute.bonus > 99
                ? 99
                : attribute.value + attribute.bonus}
            </div>
          </div>
          <AnimatedAttributeBar
            value={attribute.value}
            bonus={attribute.bonus}
          />
        </div>
      ))}
      {accelerate && (
        <div className="flex flex-col gap-y-1">
          <div className="flex flex-row items-center justify-between gap-x-2">
            <p className="text-xs mobile:text-xs text-dark-tertiary">
              AcceleRATE
            </p>
            <p className="text-xs mobile:text-xs text-dark-secondary truncate">
              {accelerate}
            </p>
          </div>
          <div className="w-full h-0.5 bg-gray-500" />
        </div>
      )}
    </div>
  );
};

interface AnimatedAttributeBarProps {
  value: number;
  bonus: number;
}

const AnimatedAttributeBar = ({ value, bonus }: AnimatedAttributeBarProps) => {
  const target = value + bonus;
  const [displayed, setDisplayed] = useState(target);

  useEffect(() => {
    setDisplayed(target);
  }, [target]);

  return (
    <div className="w-full h-0.5 bg-panel-middleground rounded overflow-hidden">
      <div
        className={cn(
          "h-full rounded transition-all duration-300",
          getAttributeColor(target),
        )}
        style={{
          width: `${(displayed / 99) * 100}%`,
        }}
      />
    </div>
  );
};

const AttributesBox = ({
  inGameAttributes,
  overallAttributes,
  isGk,
  chemistry,
  chemStyle,
  accelerate,
}: {
  inGameAttributes: {
    [label: string]: number;
  };
  overallAttributes: number[];
  isGk: boolean;
  chemistry: StateChemistry;
  chemStyle: StateChemstyle;
  accelerate: string;
}) => {
  const paceAttributes: AttributeType[] = [];
  const shootingAttributes: AttributeType[] = [];
  const passingAttributes: AttributeType[] = [];
  const dribblingAttributes: AttributeType[] = [];
  const defendingAttributes: AttributeType[] = [];
  const physicalAttributes: AttributeType[] = [];

  const statBonus = calculatePlayerStatsBonus(
    chemStyle.temporaryChemstyleId ?? chemStyle.currentChemstyleId,
    chemistry.temporaryChemistry === undefined
      ? chemistry.currentChemistry
      : chemistry.temporaryChemistry,
    inGameAttributes || {},
    overallAttributes || [],
  );

  for (const attributeLabel in inGameAttributes) {
    const attributeValue = inGameAttributes[attributeLabel];
    const attributeCategory = inGameAttributesDict[attributeLabel].category;
    switch (attributeCategory) {
      case "PACE":
        paceAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      case "SHOOTING":
        shootingAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      case "PASSING":
        passingAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      case "DRIBBLING":
        dribblingAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      case "DEFENDING":
        defendingAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      case "PHYSICALITY":
        physicalAttributes.push({
          label: inGameAttributesDict[attributeLabel].label,
          value: attributeValue,
          bonus: statBonus?.["specificStatsBonus"]?.[attributeLabel] || 0,
        });
        break;
      default:
        break;
    }
  }

  return (
    <div className="mobile:h-[460px] rounded-lg mobile:bg-black/20 grid grid-cols-2 mobile:grid-cols-3 grid-rows-2 gap-x-2 gap-y-2 mobile:gap-4 py-2">
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "DIVING" : "PACE"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[0] || 0}
          attributes={isGk ? [] : paceAttributes}
          overallAttribute={overallAttributes?.[0]}
          accelerate={isGk ? undefined : accelerate}
        />
      </div>
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "HANDLING" : "SHOOTING"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[1] || 0}
          attributes={isGk ? [] : shootingAttributes}
          overallAttribute={overallAttributes?.[1]}
        />
      </div>
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "KICKING" : "PASSING"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[2] || 0}
          attributes={isGk ? [] : passingAttributes}
          overallAttribute={overallAttributes?.[2]}
        />
      </div>
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "REFLEXES" : "DRIBBLING"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[3] || 0}
          attributes={isGk ? [] : dribblingAttributes}
          overallAttribute={overallAttributes?.[3]}
        />
      </div>
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "SPEED" : "DEFENDING"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[4] || 0}
          attributes={isGk ? paceAttributes : defendingAttributes}
          overallAttribute={overallAttributes?.[4]}
        />
      </div>
      <div className="col-span-1">
        <OverallAttribute
          sectionLabel={isGk ? "POSITIONING" : "PHYSICALITY"}
          bonusForSection={statBonus?.["allStatsBonus"]?.[5] || 0}
          attributes={isGk ? [] : physicalAttributes}
          overallAttribute={overallAttributes?.[5]}
        />
      </div>
    </div>
  );
};

interface AttributesSectionProps {
  inGameAttributes: {
    [label: string]: number;
  };
  overallAttributes: number[];
  isGk: boolean;
  chemistry: StateChemistry;
  chemStyle: StateChemstyle;
  playerHeight: number;
  handleAction: (action: DetailedPlayerAction) => void;
  chemStylesOpen: boolean;
  handleChemStylesOpen: () => void;
}

export const ChemstyleIdLabel = ({
  chemStyleId,
  best,
}: {
  chemStyleId: number;
  best: boolean;
}) => (
  <div className="flex flex-row items-center gap-2 bg-panel-middleground rounded-full pl-2 pr-3 py-1 h-7">
    <div
      className="text-xl text-dark-primary"
      style={{
        fontFamily: "UltimateTeamIcons",
      }}
    >
      {chemStylesToIcon[chemStyleId]}
    </div>
    <p className="text-xs mobile:text-xs text-dark-secondary">
      {capitalizeWords(chemStyles[chemStyleId])}
    </p>
    {best && <div className="w-3 h-3 ml-auto">{star}</div>}
  </div>
);

export const AttributesSection = ({
  inGameAttributes,
  overallAttributes,
  isGk,
  chemistry,
  chemStyle,
  playerHeight,
  handleAction,
  chemStylesOpen,
  handleChemStylesOpen,
}: AttributesSectionProps) => {
  const [accelerate, setAccelerate] = useState<string | null>(null);

  return (
    <>
      <GridBox
        title="Attributes"
        className={cn(
          chemStylesOpen ? "mobile:min-h-[708px]" : "min-h-[560px]",
          "col-span-4 touch-device:col-span-3",
        )}
      >
        <div className="flex flex-col px-2 gap-1">
          <AttributesBox
            inGameAttributes={inGameAttributes}
            overallAttributes={overallAttributes}
            isGk={isGk}
            chemistry={chemistry}
            chemStyle={chemStyle}
            accelerate={accelerate}
          />
          <div className="hidden mobile:flex flex-row gap-4 mt-2 items-center w-full px-2">
            <ChemistrySection
              selectedChemistry={chemistry.currentChemistry}
              hoveredChemistry={chemistry.temporaryChemistry}
              handleAction={handleAction}
            />
            <ChemstyleIdLabel
              chemStyleId={
                chemStyle.temporaryChemstyleId ?? chemStyle.currentChemstyleId
              }
              best={
                chemStyle.temporaryChemstyleId
                  ? chemStyle.bestChemStyleId === chemStyle.temporaryChemstyleId
                  : chemStyle.currentChemstyleId === chemStyle.bestChemStyleId
              }
            />
            <div className="ml-auto">
              <Button
                label={
                  <div className="flex flex-row items-center gap-2">
                    <span>Manage Chemstyles</span>
                    <div
                      className={cn("w-2 h-2", chemStylesOpen && "rotate-180")}
                    >
                      {arrow}
                    </div>
                  </div>
                }
                onClick={handleChemStylesOpen}
                variant="tertiary"
              />
            </div>
          </div>
          <div
            className={!chemStylesOpen ? "hidden" : "hidden mobile:block pb-2"}
          >
            {inGameAttributes && (
              <ChemStyles
                chemistry={
                  chemistry.temporaryChemistry === undefined
                    ? chemistry.currentChemistry
                    : chemistry.temporaryChemistry
                }
                height={playerHeight}
                isGk={isGk}
                inGamePlayer={inGameAttributes}
                faceCardStats={overallAttributes}
                bestChemStyle={chemStyle.bestChemStyleId}
                selectedChemStyleId={chemStyle.currentChemstyleId}
                hoveredChemStyleId={chemStyle.temporaryChemstyleId}
                handleAction={handleAction}
                setAccelerate={setAccelerate}
              />
            )}
          </div>
        </div>
      </GridBox>
      <div className="mobile:hidden flex flex-row items-center justify-between col-span-4">
        <ChemistrySection
          selectedChemistry={chemistry.currentChemistry}
          hoveredChemistry={chemistry.temporaryChemistry}
          handleAction={handleAction}
        />
        <Button
          icon={
            <div className={cn("w-2 h-2", chemStylesOpen && "rotate-180")}>
              {arrow}
            </div>
          }
          label="Chemstyles"
          onClick={handleChemStylesOpen}
          variant="tertiary"
        />
      </div>
      <div
        className={cn(
          "mobile:hidden items-center justify-between col-span-4 overflow-x-auto -mt-2 -mx-4 px-4",
          chemStylesOpen ? "flex flex-row" : "hidden",
        )}
      >
        {inGameAttributes && (
          <ChemStyles
            chemistry={
              chemistry.temporaryChemistry === undefined
                ? chemistry.currentChemistry
                : chemistry.temporaryChemistry
            }
            height={playerHeight}
            isGk={isGk}
            inGamePlayer={inGameAttributes}
            faceCardStats={overallAttributes}
            bestChemStyle={chemStyle.bestChemStyleId}
            selectedChemStyleId={chemStyle.currentChemstyleId}
            hoveredChemStyleId={chemStyle.temporaryChemstyleId}
            handleAction={handleAction}
            setAccelerate={setAccelerate}
          />
        )}
      </div>
    </>
  );
};
